import { graphql, Link } from "gatsby";
import { Trans, useI18next } from "@herob191/gatsby-plugin-react-i18next";
import * as React from "react";
import AppStoreButton from "../components/AppStoreButton/AppStoreButton";
import GooglePlayButton from "../components/GooglePlayButton/GooglePlayButton";
import Layout from "../components/layout";
import { SEO } from "../components/seo";

// Import images
import classicImg from "../images/universes/classic.png";
import constructionImg from "../images/universes/construction.png";
import creativeImg from "../images/universes/creative.png";
import environmentImg from "../images/universes/environment.png";
import inventorsImg from "../images/universes/inventors.png";
import christmasImg from "../images/universes/christmas.jpg";

const StoryCategory = ({ title, description, link, backgroundImage }) => (
  <Link 
    to={link} 
    className="block relative overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
    style={{ height: '280px' }}
  >
    <div 
      className="absolute inset-0 bg-cover bg-center z-0"
      style={{ 
        backgroundImage: `url(${backgroundImage})`,
        filter: 'brightness(0.85)'
      }}
    />
    <div className="relative z-10 h-full p-6 flex flex-col justify-between bg-gradient-to-t from-black/70 to-transparent">
      <div className="flex flex-col items-center justify-center sm:justify-start">
        
        <h2 className="text-xl font-bold text-white text-center sm:text-left">{title}</h2>
      </div>
      <p className="text-white/90 text-center sm:text-left">{description}</p>
    </div>
  </Link>
);

export default function StoryCategoriesOverview() {
  const { language } = useI18next();
  const appleURL = `https://apps.apple.com/app/apple-store/id1663618939?pt=124054911&ct=blogPost&mt=8`;
  const googlePlayUrl = `https://play.google.com/store/apps/details?id=com.heyqqgmbh.oscarai&referrer=utm_source=blogPost%26utm_medium=blogPost%26utm_term=blogPost`;

  const categories = [
    {
      title: <Trans ns="storyCategory">Creative Children Stories</Trans>,
      description: <Trans ns="storyCategory">Browse our collection of creative bedtime stories</Trans>,
      link: "/stories",
      icon: "📚",
      backgroundImage: classicImg,
    },
    {
      title: <Trans ns="storyCategory">Construction Stories</Trans>,
      description: <Trans ns="storyCategory">Adventures with trucks, builders, and big machines</Trans>,
      link: "/constructionStories",
      icon: "🚧",
      backgroundImage: constructionImg,
    },
    {
      title: <Trans ns="storyCategory">Educational Stories</Trans>,
      description: <Trans ns="storyCategory">Stories that teach and inspire young minds</Trans>,
      link: "/educationalStories",
      icon: "📖",
      backgroundImage: inventorsImg,
    },
    {
      title: <Trans ns="storyCategory">Environmental Stories</Trans>,
      description: <Trans ns="storyCategory">Tales about nature and protecting our planet</Trans>,
      link: "/environmentStories",
      icon: "🌍",
      backgroundImage: environmentImg,
    },
    {
      title: <Trans ns="storyCategory">Funny Stories</Trans>,
      description: <Trans ns="storyCategory">Hilarious adventures that will make your child laugh</Trans>,
      link: "/funnyStories",
      icon: "😄",
      backgroundImage: creativeImg,
    },
    {
      title: <Trans ns="storyCategory">Christmas Stories</Trans>,
      description: <Trans ns="storyCategory">Magical holiday tales full of wonder</Trans>,
      link: "/christmasStories",
      icon: "🎄",
      backgroundImage: christmasImg,
    },
  ];

  return (
    <Layout>
      <main>
        <section className="hero text-center text-light">
          <div className="container-sm">
            <div className="hero-inner">
              <div className="hero-copy">
                <h1 className="my-4 font-bold text-3xl">
                  <Trans ns="storyCategory">The best Children Stories</Trans>
                </h1>
                <div className="text my-4 mx-6 text-left" style={{ whiteSpace: "pre-line" }}>
                  <p>
                    <Trans ns="storyCategory">
                      Welcome to our enchanting world of children's stories! Discover a carefully curated collection of
                      tales designed to make bedtime magical and educational for your little ones.
                    </Trans>
                  </p>

                  <p className="mt-4">
                    <Trans ns="storyCategory">Our story categories offer something special for every child:</Trans>
                  </p>

                  <p className="mt-4">
                    🌟 <Trans ns="storyCategory">Creative Children Stories</Trans>:{" "}
                    <Trans ns="storyCategory">
                      A complete collection of bedtime tales that spark imagination and create lasting memories.
                    </Trans>
                  </p>

                  <p className="mt-2">
                    🚧 <Trans ns="storyCategory">Construction Stories</Trans>:{" "}
                    <Trans ns="storyCategory">
                      Exciting adventures featuring trucks, builders, and big machines that bring construction sites to
                      life.
                    </Trans>
                  </p>

                  <p className="mt-2">
                    📖 <Trans ns="storyCategory">Educational Stories</Trans>:{" "}
                    <Trans ns="storyCategory">
                      Engaging tales that combine learning with fun, helping children discover new concepts while being
                      entertained.
                    </Trans>
                  </p>

                  <p className="mt-2">
                    🌍 <Trans ns="storyCategory">Environmental Stories</Trans>:{" "}
                    <Trans ns="storyCategory">
                      Beautiful narratives that teach children about nature, sustainability, and caring for our planet.
                    </Trans>
                  </p>

                  <p className="mt-2">
                    😄 <Trans ns="storyCategory">Funny Stories</Trans>:{" "}
                    <Trans ns="storyCategory">
                      Delightful tales filled with humor and joy that will have your children giggling at bedtime.
                    </Trans>
                  </p>

                  <p className="mt-2">
                    🎄 <Trans ns="storyCategory">Christmas Stories</Trans>:{" "}
                    <Trans ns="storyCategory">
                      Magical holiday adventures that capture the wonder and spirit of the festive season.
                    </Trans>
                  </p>

                  <p className="mt-6">
                    <Trans ns="storyCategory">
                      Each story is crafted using Oscar Stories' innovative AI technology, ensuring unique and engaging
                      content that adapts to your child's interests. Our tales are more than just bedtime stories,
                      they're gateways to learning, imagination, and family bonding.
                    </Trans>
                  </p>

                  <p className="mt-4">
                    <Trans ns="storyCategory">
                      Ready to explore? Choose a category and start your storytelling adventure today!
                    </Trans>
                  </p>
                </div>

                <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                  {categories.map((category, index) => (
                    <StoryCategory key={index} {...category} />
                  ))}
                </div>

                <div className="mt-12 featureContainer p-8 rounded-lg">
                  <h2 className="text-2xl font-bold mb-4">
                    <Trans ns="storyCategory">Create Your Own Personalized Stories!</Trans>
                  </h2>
                  <p className="mb-6">
                    <Trans ns="storyCategory">
                      Loved these stories? Imagine creating personalized adventures starring your own child! With the
                      Oscar Stories app, you can generate unique, AI-powered tales tailored to your little one's
                      interests. Download now and start your magical storytelling journey!
                    </Trans>
                  </p>
                  <section id="download" className="my-2 md:mt-4 justify-center items-center flex">
                    <div className="justify-center sm:justify-start align-middle flex flex-wrap">
                      <AppStoreButton lang={language} url={appleURL} />
                      <GooglePlayButton lang={language} url={googlePlayUrl} />
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  );
}

export const Head = ({ pageContext, data }) => {
  const mergedLocales = data.locales.edges.reduce((acc, edge) => {
    return { ...acc, ...JSON.parse(edge.node.data) };
  }, {});

  const language = pageContext?.i18n?.language || "en";

  const description =
    mergedLocales[
      "Welcome to our enchanting world of children's stories! Discover a carefully curated collection of tales designed to make bedtime magical and educational for your little ones. Our story categories offer something special for every child."
    ] ||
    "Welcome to our enchanting world of children's stories! Discover a carefully curated collection of tales designed to make bedtime magical and educational for your little ones. Our story categories offer something special for every child.";

  return (
    <SEO 
      title={mergedLocales["The best Children Stories"] || "The Best Children Stories - Free Bedtime Tales for Kids"} 
      description={description} 
      lang={language} 
    />
  );
};

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: { ns: { in: ["common", "storyCategory"] }, language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;
